import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, ShoppingBag, Heart, Settings as SettingsIcon, Play, Download, Ticket, Copy, Radio, QrCode, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { api, formatZAR, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

function ChangePasswordForm({ authProvider }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [busy, setBusy] = useState(false);
  const isGoogleOnly = authProvider === "google";

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password changed successfully");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  if (isGoogleOnly) {
    return (
      <div className="bg-white border border-gray-200 p-8" data-testid="change-password-disabled">
        <Lock className="w-6 h-6 text-[#1F3864] mb-4" />
        <h3 className="font-display text-2xl text-[#1F3864] mb-3">Change Password</h3>
        <div className="text-sm text-gray-600 p-4 bg-[#F5F5F5] border-l-4 border-[#1F3864]">
          Your account is signed in with Google. Password changes are managed in your Google account settings.
        </div>
      </div>
    );
  }

  const fieldLabel = (k) => ({ current: "Current Password", next: "New Password", confirm: "Confirm New Password" }[k]);
  const fieldKey = (k) => ({ current: "current", next: "next", confirm: "confirm" }[k]);

  return (
    <form onSubmit={submit} className="bg-white border border-gray-200 p-8" data-testid="form-change-password">
      <Lock className="w-6 h-6 text-[#1F3864] mb-4" />
      <h3 className="font-display text-2xl text-[#1F3864] mb-2">Change Password</h3>
      <p className="text-sm text-gray-600 mb-6">Use at least 8 characters. Mix letters, numbers and symbols for stronger security.</p>
      <div className="space-y-4">
        {["current", "next", "confirm"].map((k) => (
          <div key={k}>
            <label className="block text-xs tracking-[0.15em] uppercase text-gray-600 mb-1.5">{fieldLabel(k)}</label>
            <div className="relative">
              <input
                type={show[k] ? "text" : "password"}
                required
                value={form[fieldKey(k)]}
                onChange={(e) => setForm({ ...form, [fieldKey(k)]: e.target.value })}
                className="w-full px-3 py-2.5 pr-10 border border-gray-300 focus:border-[#1F3864] outline-none text-sm"
                data-testid={`input-password-${k}`}
                autoComplete={k === "current" ? "current-password" : "new-password"}
              />
              <button
                type="button"
                onClick={() => setShow({ ...show, [k]: !show[k] })}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-[#1F3864]"
                tabIndex={-1}
              >
                {show[k] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ))}
        {form.next && form.confirm && form.next === form.confirm && form.next.length >= 8 && (
          <div className="flex items-center gap-2 text-xs text-green-700">
            <CheckCircle2 className="w-4 h-4" /> Passwords match — ready to save
          </div>
        )}
        <button type="submit" disabled={busy} className="btn-primary-red" data-testid="action-change-password-submit">
          {busy ? "Saving..." : "Update Password"}
        </button>
      </div>
    </form>
  );
}

export default function MemberDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [purchases, setPurchases] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [membership, setMembership] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [eventAccess, setEventAccess] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [qrTicket, setQrTicket] = useState(null);

  useEffect(() => {
    api.get("/user/purchases").then((r) => setPurchases(r.data)).catch(() => {});
    api.get("/user/watchlist").then((r) => setWatchlist(r.data)).catch(() => {});
    api.get("/user/membership").then((r) => setMembership(r.data)).catch(() => {});
    api.get("/user/downloads").then((r) => setDownloads(r.data)).catch(() => {});
    api.get("/user/event-access").then((r) => setEventAccess(r.data)).catch(() => {});
    api.get("/user/tickets").then((r) => setTickets(r.data)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="py-32 text-center">
        <h2 className="font-display text-3xl text-[#1F3864] mb-4">Please sign in</h2>
        <Link to="/login" className="btn-primary-red">Sign In</Link>
      </div>
    );
  }

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <div className="bg-[#F5F5F5] min-h-[80vh]">
      <section className="bg-[#1F3864] text-white py-16" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-3">Member Dashboard</div>
            <h1 className="font-display text-4xl sm:text-5xl">Welcome, {user.name.split(" ")[0]}.</h1>
          </div>
          {membership?.active ? (
            <div className="flex items-center gap-3 bg-white/5 border border-white/20 px-5 py-3">
              <Crown className="w-5 h-5 text-[#C00000]" />
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-white/60">Active Plan</div>
                <div className="font-medium capitalize">{membership.tier} Member</div>
              </div>
            </div>
          ) : (
            <Link to="/membership" className="btn-primary-red" data-testid="action-upgrade-membership">
              <Crown className="w-4 h-4" /> Become A Member
            </Link>
          )}
        </div>
      </section>

      <section className="py-8 border-b border-gray-200 bg-white sticky top-20 z-20" data-testid="dashboard-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 flex-wrap">
          {[
            { key: "overview", label: "Overview" },
            { key: "tickets", label: "My Tickets" },
            { key: "events", label: "Live Access" },
            { key: "downloads", label: "Downloads" },
            { key: "purchases", label: "Purchases" },
            { key: "watchlist", label: "Watchlist" },
            { key: "settings", label: "Settings" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border ${
                tab === t.key ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-700"
              }`}
              data-testid={`dashboard-tab-${t.key}`}
            >{t.label}</button>
          ))}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {tab === "overview" && (
            <div className="grid md:grid-cols-3 gap-6" data-testid="dashboard-overview">
              <div className="bg-white p-6 border-l-4 border-[#C00000]">
                <ShoppingBag className="w-6 h-6 text-[#1F3864] mb-3" />
                <div className="text-3xl font-display text-[#1F3864]">{purchases.length}</div>
                <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mt-1">Purchases</div>
              </div>
              <div className="bg-white p-6 border-l-4 border-[#C00000]">
                <Heart className="w-6 h-6 text-[#1F3864] mb-3" />
                <div className="text-3xl font-display text-[#1F3864]">{watchlist.length}</div>
                <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mt-1">Watchlist Items</div>
              </div>
              <div className="bg-white p-6 border-l-4 border-[#C00000]">
                <Crown className="w-6 h-6 text-[#1F3864] mb-3" />
                <div className="text-3xl font-display text-[#1F3864]">
                  {membership?.active ? "Active" : "Free"}
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mt-1">Membership</div>
              </div>
            </div>
          )}

          {tab === "tickets" && (
            <div data-testid="dashboard-tickets">
              <h2 className="font-display text-2xl text-[#1F3864] mb-6">Your Event Tickets</h2>
              {tickets.length === 0 ? (
                <div className="bg-white border border-gray-200 p-10 text-center text-gray-500">
                  No tickets yet. <Link to="/events" className="text-[#C00000] hover:underline">Browse upcoming events</Link>.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {tickets.map((t) => {
                    const isPast = t.event?.event_date && new Date(t.event.event_date) < new Date();
                    const isUsed = t.status === "used";
                    return (
                      <div key={t.id} className="bg-white border border-gray-200 overflow-hidden" data-testid={`ticket-${t.id}`}>
                        {t.event?.image_url && (
                          <div className="aspect-video bg-gray-100 overflow-hidden relative">
                            <img src={t.event.image_url.startsWith("/api/") ? `${BACKEND_URL}${t.event.image_url}` : t.event.image_url} alt="" className="w-full h-full object-cover" />
                            {(isPast || isUsed) && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <div className="text-white text-xs tracking-[0.3em] uppercase border border-white/40 px-3 py-1">
                                  {isUsed ? "Used" : "Past Event"}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="p-5">
                          <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-1">{t.tier_name || "General Admission"}</div>
                          <h3 className="font-display text-lg text-[#1F3864] mb-2 leading-tight">{t.event?.title || t.event_title}</h3>
                          {t.event?.event_date && (
                            <div className="text-xs text-gray-500 mb-1">
                              {new Date(t.event.event_date).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}
                            </div>
                          )}
                          {t.event?.venue && <div className="text-xs text-gray-500 mb-3">{t.event.venue}</div>}
                          <div className="bg-[#F5F5F5] p-3 my-3 text-center">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-1">Ticket Code</div>
                            <code className="font-mono text-lg tracking-[0.2em] text-[#1F3864]">{t.ticket_code}</code>
                          </div>
                          <button onClick={() => setQrTicket(t)} className="btn-primary-navy w-full justify-center !py-2 !text-xs" data-testid={`action-show-qr-${t.id}`}>
                            <QrCode className="w-4 h-4" /> Show QR for entry
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {qrTicket && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setQrTicket(null)}>
                  <div className="bg-white max-w-md w-full p-8 text-center" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-3">Entry Pass</div>
                    <h3 className="font-display text-2xl text-[#1F3864] mb-1">{qrTicket.event?.title}</h3>
                    <div className="text-xs text-gray-500 mb-5">{qrTicket.tier_name}</div>
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=NGP-${qrTicket.ticket_code}&bgcolor=ffffff&color=1F3864&qzone=2`}
                      alt={`QR for ${qrTicket.ticket_code}`}
                      className="mx-auto"
                    />
                    <code className="block font-mono text-xl tracking-[0.3em] text-[#1F3864] mt-5">{qrTicket.ticket_code}</code>
                    <div className="text-xs text-gray-500 mt-4">Present this QR code at the venue door. Each code admits one person.</div>
                    <button onClick={() => setQrTicket(null)} className="btn-outline-navy mt-6">Close</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "downloads" && (
            <div data-testid="dashboard-downloads">
              <h2 className="font-display text-2xl text-[#1F3864] mb-6">Your Digital Downloads</h2>
              {downloads.length === 0 ? (
                <div className="bg-white border border-gray-200 p-10 text-center text-gray-500">
                  No digital downloads yet. <Link to="/shop" className="text-[#C00000] hover:underline">Browse digital music</Link>.
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-5">
                  {downloads.map((d) => {
                    const expired = d.download_expires_at && new Date(d.download_expires_at) < new Date();
                    const exhausted = (d.download_count || 0) >= (d.download_max || 5);
                    const canDownload = !expired && !exhausted && d.download_token;
                    const href = `${BACKEND_URL}/api/download/${d.download_token}`;
                    return (
                      <div key={d.id} className="bg-white border border-gray-200 p-6" data-testid={`download-${d.id}`}>
                        <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-2">Digital Download</div>
                        <h3 className="font-display text-lg text-[#1F3864] mb-3">{d.product_name}</h3>
                        <div className="text-xs text-gray-500 mb-1">Downloads used: {d.download_count || 0} / {d.download_max || 5}</div>
                        {d.download_expires_at && (
                          <div className={`text-xs mb-4 ${expired ? "text-[#C00000]" : "text-gray-500"}`}>
                            {expired ? "Expired" : `Expires: ${new Date(d.download_expires_at).toLocaleDateString()}`}
                          </div>
                        )}
                        {canDownload ? (
                          <a href={href} className="btn-primary-red !py-2 !px-4 !text-sm inline-flex" data-testid={`action-download-${d.id}`}>
                            <Download className="w-4 h-4" /> Download
                          </a>
                        ) : (
                          <div className="text-xs text-[#C00000] font-medium">
                            {expired ? "Link expired" : exhausted ? "Download limit reached (5)" : "Download unavailable"}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "events" && (
            <div data-testid="dashboard-events">
              <h2 className="font-display text-2xl text-[#1F3864] mb-6">Your Event Access</h2>
              {eventAccess.length === 0 ? (
                <div className="bg-white border border-gray-200 p-10 text-center text-gray-500">
                  No event tickets yet. <Link to="/events" className="text-[#C00000] hover:underline">Browse upcoming events</Link>.
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-5">
                  {eventAccess.map((a) => {
                    const expired = a.expires_at && new Date(a.expires_at) < new Date();
                    return (
                      <div key={a.id} className="bg-white border border-gray-200 overflow-hidden" data-testid={`event-access-${a.id}`}>
                        {a.event?.image_url && (
                          <div className="aspect-video bg-gray-100 overflow-hidden">
                            <img src={a.event.image_url.startsWith("/api/") ? `${BACKEND_URL}${a.event.image_url}` : a.event.image_url} alt="" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="p-5">
                          <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-2 flex items-center gap-2">
                            <Ticket className="w-3.5 h-3.5" /> PPV Access
                          </div>
                          <h3 className="font-display text-lg text-[#1F3864] mb-2">{a.event?.title || "Event"}</h3>
                          {a.event?.event_date && (
                            <div className="text-xs text-gray-500 mb-3">
                              {new Date(a.event.event_date).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })} · {a.event.venue}
                            </div>
                          )}
                          <div className="mb-3">
                            <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-1">Access Code</div>
                            <div className="flex items-center gap-2">
                              <code className="px-3 py-2 bg-[#F5F5F5] border border-gray-200 font-mono text-lg tracking-widest text-[#1F3864]">
                                {a.access_code || "—"}
                              </code>
                              <button onClick={() => copy(a.access_code)} className="p-2 border border-gray-300 hover:border-[#1F3864]" title="Copy">
                                <Copy className="w-4 h-4 text-[#1F3864]" />
                              </button>
                            </div>
                          </div>
                          <div className={`text-xs mb-3 ${expired ? "text-[#C00000]" : "text-gray-500"}`}>
                            {expired ? "Access expired" : `Valid until: ${new Date(a.expires_at).toLocaleString("en-ZA")}`}
                          </div>
                          {!expired && a.access_code && (
                            <Link to={`/events/${a.event_id}/stream?code=${a.access_code}`} className="btn-primary-red !py-2 !px-4 !text-sm inline-flex" data-testid={`action-watch-stream-${a.id}`}>
                              <Radio className="w-4 h-4" /> Watch Stream
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "purchases" && (
            <div className="bg-white border border-gray-200" data-testid="dashboard-purchases">
              {purchases.length === 0 ? (
                <div className="p-10 text-center text-gray-500">No purchases yet. <Link to="/shop" className="text-[#C00000] hover:underline">Visit the shop</Link>.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr>
                    <th className="text-left p-4">Product</th>
                    <th className="text-left p-4">Amount</th>
                    <th className="text-left p-4">Date</th>
                    <th className="text-left p-4">Status</th>
                  </tr></thead>
                  <tbody>
                    {purchases.map((p) => (
                      <tr key={p.id} className="border-t border-gray-100">
                        <td className="p-4">{p.product_name}</td>
                        <td className="p-4">{formatZAR(p.amount)}</td>
                        <td className="p-4">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="p-4"><span className="px-2 py-1 bg-green-100 text-green-700 text-xs">Completed</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === "watchlist" && (
            <div data-testid="dashboard-watchlist">
              {watchlist.length === 0 ? (
                <div className="py-20 text-center text-gray-500">Your watchlist is empty. <Link to="/videos" className="text-[#C00000] hover:underline">Browse videos</Link>.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {watchlist.map((v) => (
                    <Link to="/videos" key={v.id} className="group block">
                      <div className="aspect-video overflow-hidden bg-gray-100 relative">
                        <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
                        <Play className="w-12 h-12 text-white absolute inset-0 m-auto opacity-0 group-hover:opacity-100 transition-opacity" fill="white" />
                      </div>
                      <h3 className="font-display text-lg text-[#1F3864] mt-3 leading-snug">{v.title}</h3>
                      <div className="text-xs text-gray-500 mt-1">{v.artist_name}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "settings" && (
            <div className="space-y-6 max-w-2xl" data-testid="dashboard-settings">
              <div className="bg-white border border-gray-200 p-8">
                <SettingsIcon className="w-6 h-6 text-[#1F3864] mb-4" />
                <h3 className="font-display text-2xl text-[#1F3864] mb-6">Account Details</h3>
                <div className="space-y-4 text-sm">
                  <div><div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-1">Name</div><div>{user.name}</div></div>
                  <div><div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-1">Email</div><div>{user.email}</div></div>
                  <div><div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-1">Sign-in Method</div><div className="capitalize">{user.auth_provider || "local"}</div></div>
                  <div><div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-1">Member Since</div><div>{new Date(user.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })}</div></div>
                </div>
              </div>
              <ChangePasswordForm authProvider={user.auth_provider} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
